import React, { useState, useEffect } from "react";
import { Table, Thead, Tr, Th, Sortable } from "@lightspeed/cirrus-table";
import { Card } from "@lightspeed/flame/Card";
import Row from "./categoriesTableRound/Row";
import Loader from "../Loader";
import { useSelector } from "react-redux";
import * as sortFns from "../../utils/sortFn";

const CategoriesTableRound = ({
  data,
  sortState,
  changeSortState,
  scrollPos,
}) => {
  const ui = useSelector((state) => state.ui);

  const [sortedData, setSortedData] = useState([...data]);
  const sortValues = ["unsorted", "desc", "asc"];
  const reducer = (accumulator, currentValue) => accumulator + currentValue;

  const hasParentPos = data?.some(
    (item) => (item.parentid ?? "") !== ""
  );

  const posColsCount = hasParentPos ? 4 : 3;
  const accountingColsCount = hasParentPos ? 3 : 4;

  useEffect(() => {
    setSortedData([...data]);
  }, [data]);

  useEffect(() => {
    if (sortState.reduce(reducer) > 0) {
      let newData = [...data];

      if (sortState[0] === 1) {
        newData = [...newData.sort(sortFns.customSortGen("company_id"))];
      } else if (sortState[0] === 2) {
        newData = [...newData.sort(sortFns.customSortGen("company_id")).reverse()];
      }

      if (sortState[1] === 1) {
        newData = [...newData.sort(sortFns.customSortGen("category_id"))];
      } else if (sortState[1] === 2) {
        newData = [...newData.sort(sortFns.customSortGen("category_id")).reverse()];
      }

      if (sortState[2] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("category_name"))];
      } else if (sortState[2] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("category_name")).reverse()];
      }

      if (sortState[3] === 1) {
        newData = [...newData.sort(sortFns.customSortGen("parentid"))];
      } else if (sortState[3] === 2) {
        newData = [...newData.sort(sortFns.customSortGen("parentid")).reverse()];
      }

      if (sortState[4] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("account_id"))];
      } else if (sortState[4] === 2) {
        newData = [...newData.sort(sortFns.customSortGenString("account_id")).reverse()];
      }

      if (sortState[5] === 1) {
        newData = [...newData.sort(sortFns.customSortGenString("account_description"))];
      } else if (sortState[5] === 2) {
        newData = [
          ...newData.sort(sortFns.customSortGenString("account_description")).reverse(),
        ];
      }

      setSortedData(newData);
    } else {
      setSortedData([...data]);
    }
  }, [sortState, data]);

  if (sortedData.length === 0 && ui.search) {
    return (
      <div className="loader-no-item">
        <h3>No items match your search</h3>
      </div>
    );
  }

  if (!sortedData || sortedData.length === 0 || !Array.isArray(sortedData)) {
    return <Loader />;
  }

  return (
    <Card top="true">
      <div className="payment-table-wrap">
        <Table>
          <Thead>
            <Tr className={`section-row ${ui.theme === "flame" ? "" : "dark-table-section"}`}>
              <Th colSpan={posColsCount} className="section-title section-title-pos">
                <div className="section-badge">POS</div>
              </Th>
              <Th colSpan={accountingColsCount} className="section-title section-title-acc">
                <div className="section-badge">Accounting</div>
              </Th>
            </Tr>

            {scrollPos === true ? (
              <Tr className={`${ui.theme === "flame" ? "" : "dark-table"}`}>
                <Sortable.Th
                  direction={sortValues[sortState[0]]}
                  onClick={() => {
                    changeSortState(0);
                  }}
                  className="th-pos"
                >
                  Company
                </Sortable.Th>

                <Sortable.Th
                  direction={sortValues[sortState[1]]}
                  onClick={() => {
                    changeSortState(1);
                  }}
                  className="th-pos"
                >
                  Category Id
                </Sortable.Th>

                <Sortable.Th
                  direction={sortValues[sortState[2]]}
                  onClick={() => {
                    changeSortState(2);
                  }}
                  className={hasParentPos ? "th-pos" : "th-pos th-pos-last"}
                >
                  Category Name
                </Sortable.Th>

                {hasParentPos && (
                  <Sortable.Th
                    direction={sortValues[sortState[3]]}
                    onClick={() => {
                      changeSortState(3);
                    }}
                    className="th-pos th-pos-last"
                  >
                    Parent
                  </Sortable.Th>
                )}

                <Sortable.Th
                  direction={sortValues[sortState[4]]}
                  onClick={() => {
                    changeSortState(4);
                  }}
                  className="th-acc th-acc-first"
                >
                  Account
                </Sortable.Th>

                <Sortable.Th
                  direction={sortValues[sortState[5]]}
                  onClick={() => {
                    changeSortState(5);
                  }}
                  className="th-acc"
                >
                  Account Description
                </Sortable.Th>

                <Th textAlign="right" className="th-acc th-plain th-action">
                  Action
                </Th>
              </Tr>
            ) : (
              <Tr className={`${ui.theme === "flame" ? "" : "dark-table-fake"}`}>
                <Th className="table-th-sticky th-pos">Company</Th>
                <Th className="table-th-sticky th-pos">Category Id</Th>
                <Th className={`table-th-sticky ${hasParentPos ? "th-pos" : "th-pos th-pos-last"}`}>
                  Category Name
                </Th>

                {hasParentPos && (
                  <Th className="table-th-sticky th-pos th-pos-last">Parent</Th>
                )}

                <Th className="table-th-sticky th-acc th-acc-first">Account</Th>
                <Th className="table-th-sticky th-acc">Account Description</Th>
                <Th className="table-th-sticky th-acc th-plain th-action" textAlign="right">
                  Action
                </Th>
              </Tr>
            )}
          </Thead>

          <tbody className="t-body">
            {sortedData.map((item, index) => (
              <Row
                key={`${item.company_id}-${item.category_id}-${item.parentid ?? "noparent"}`}
                item={item}
                rowIndex={index}
              />
            ))}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

export default CategoriesTableRound;